import React, { useCallback, useMemo, useState } from 'react';

import { more, trendDown } from '@/assets/icons';
// import { upRightFromSquare } from '@/assets/icons';
import AppleMusicBlack from '@/assets/images/applemusic-black.svg?react';
import AppleMusicWhite from '@/assets/images/applemusic-white.svg?react';
import Spotify from '@/assets/images/spotify.svg?react';
import YouTubeBlack from '@/assets/images/youtube-black.svg?react';
import YouTubeWhite from '@/assets/images/youtube-white.svg?react';
import { SetlistSummaryCalendar } from '@/components/SetlistSummaryCard';
import { getFeedbackEmptyStateConfig } from '@/components/SetlistSummaryCard/utils';
import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import {
	BottomCard,
	BottomCardAction,
	BottomCardForm,
} from '@/components/shared/BottomCard';
import { AsaphButton } from '@/components/shared/Button';
import { Chip } from '@/components/shared/Chip';
import { FeedbackScale } from '@/components/shared/FeedbackScale';
import { Legend, LegendItem } from '@/components/shared/Legend';
import { LineChart } from '@/components/shared/LineChart';
import { ModalSectionCard } from '@/components/shared/ModalSectionCard';
import { ModalSectionSecondaryText } from '@/components/shared/ModalSectionSecondaryText';
import { ModalSectionTitle } from '@/components/shared/ModalSectionTitle';
import { QuartileScales } from '@/components/shared/QuartileScales/QuartileScales';
import { getRotationStatusClassName } from '@/components/shared/RotationStatusLabel';
import {
	SettingSectionTitle,
	SettingsList,
	SettingsListItems,
} from '@/components/shared/SettingCard';
import { SongThemeChip } from '@/components/shared/SongThemeChip';
import { AsaphSwitch } from '@/components/shared/Switch';
import { Tabs } from '@/components/shared/Tabs';
import { TrendIcon } from '@/components/TrendIcon';
import { ROTATION_STATUSES } from '@/config/constants';
import {
	Permissions,
	useAddSongsToMyTeamMutation,
	useReanalyzeSongMutation,
} from '@/graphql';
import { generateAlbumGradient } from '@/helpers/albumGradient';
import { deriveTrend, FLAT_THRESHOLD } from '@/helpers/deriveTrend';
import { birthYearToGeneration } from '@/helpers/reportsDataHelpers';
import { getAcronym } from '@/helpers/textTransform';
import { useDarkMode } from '@/hooks/useDarkMode';
import { FeedbackPanel } from '@/pages/Dashboard/components/FeedbackPanel';
import { DistributionCirclePackingChart } from '@/pages/Reports/components/DistributionCirclePackingChart/DistributionCirclePackingChart';
import { ReportsFilterBar } from '@/pages/Reports/components/ReportsFilterBar/ReportsFilterBar';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { SongData } from '@/providers/AnalyticsProvider/events';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useServices } from '@/providers/ServicesProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useTeam } from '@/providers/TeamProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { IconButton, List, SvgIcon } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { mean } from 'mathjs';
import { useSnackbar } from 'notistack';
import { groupBy } from 'ramda';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';

import { ESongModalDisplayState } from '../SongModalDisplayState';
import {
	IPropsSongModalDisplay,
	LimitedTeamSongFeedbackReport,
	SongModalSong,
} from '../SongModalPanel';
import { SongEmotions } from './SongEmotions';
import styles from './SongModalRoot.module.scss';

const TOP_ITEMS_HEIGHT = 130;

export const SongModalRoot: React.FC<IPropsSongModalDisplay> = ({
	openedSong,
	pushDisplayState,
	setOpen,
	setTitleVisible,
	titleVisible,
	handleClose,
	isTeamSong,
	onSettingsClick,
	updateTeamSong,
	setOpenedSong,
}) => {
	const { mutateAsync: addSongsToMyTeam, isLoading } =
		useAddSongsToMyTeamMutation();
	const { team } = useTeam();
	const { hasPermission } = usePermissions();
	const { isDarkModeOn } = useDarkMode();
	const { enqueueSnackbar } = useSnackbar();
	const { refetch: refetchTeamSongs } = useTeamSongs();
	const queryClient = useQueryClient();
	const { setlists } = useSetlists();
	const { services } = useServices();

	const fireRequestAnalysisEvent = useAnalyticsEvent(
		'View_Song_Request_Analysis',
	);
	const { mutateAsync: reanalyzeSong, isLoading: isReanalyzing } =
		useReanalyzeSongMutation({
			onSuccess: (data) => {
				setOpenedSong((prev) =>
					prev
						? {
								...prev,
								analysisOutput:
									data.reanalyzeSong.analysisOutput,
								analysisStatus:
									data.reanalyzeSong.analysisStatus,
						  }
						: prev,
				);
			},
			onError: () => {
				enqueueSnackbar('Failed to generate analysis', {
					variant: 'error',
				});
			},
		});

	const [tabIndex, setTabIndex] = useState(0);
	const [isListenOpen, setIsListenOpen] = useState(false);
	const [infoSheet, setInfoSheet] = useState<'energy' | 'songType' | null>(
		null,
	);

	// Feedback empty state — tracks which setlist to open FeedbackPanel for
	const [feedbackSetlistId, setFeedbackSetlistId] = useState<string | null>(
		null,
	);

	// Feedback filter state — declared at SongModalRoot level so it persists across tab switches
	const [skillFilter, setSkillFilter] = useState<string[]>([]);
	const [genderFilter, setGenderFilter] = useState<string[]>([]);
	const [generationFilter, setGenerationFilter] = useState<string[]>([]);
	const [serviceFilter, setServiceFilter] = useState<string[]>([]);

	const fireAddSongEvent = useAnalyticsEvent('View_Song_Add_Song');
	const fireOpenChartsEvent = useAnalyticsEvent('View_Song_Open_Charts');
	const fireOpenYTEvent = useAnalyticsEvent('View_Song_Open_YouTube');
	const fireOpenAppleMusicEvent = useAnalyticsEvent(
		'View_Song_Open_AppleMusic',
	);
	const fireOpenSpotifyEvent = useAnalyticsEvent('View_Song_Open_Spotify');
	const fireViewTab = useAnalyticsEvent('View_Song_View_Tab');
	const fireFilterSkill = useAnalyticsEvent('View_Song_Filter_Skill');
	const fireFilterGender = useAnalyticsEvent('View_Song_Filter_Gender');
	const fireFilterGeneration = useAnalyticsEvent(
		'View_Song_Filter_Generation',
	);
	const fireFilterService = useAnalyticsEvent('View_Song_Filter_Service');
	const fireClearFilters = useAnalyticsEvent('View_Song_Clear_Filters');

	const eventData = useMemo<SongData>(
		() => ({
			id: openedSong.id,
			artist: openedSong.artist,
			title: openedSong.name,
		}),
		[openedSong],
	);

	const sentimentReports = useMemo(
		() =>
			prepareSentimentReports(
				openedSong.feedback?.teamSongFeedbackReports ?? [],
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[openedSong.feedback?.teamSongFeedbackReports?.length],
	);

	const handleGoToChards = () => {
		if (team.chartRepositoryUrl) {
			window.open(team.chartRepositoryUrl, '_blank')?.focus();
			fireOpenChartsEvent();
		}
	};

	const handleAdd = useCallback(async () => {
		try {
			await addSongsToMyTeam({
				songIds: openedSong.id,
			});
			enqueueSnackbar('Song added to the songlist', {
				variant: 'success',
			});
			fireAddSongEvent(eventData);
		} catch (e) {
			enqueueSnackbar(
				'Something went wrong while updating the songlist',
				{
					variant: 'error',
				},
			);
			return;
		} finally {
			queryClient.invalidateQueries({
				queryKey: ['ListMyTeamSongs'],
			});
			setOpen(false);
		}
	}, [
		addSongsToMyTeam,
		openedSong.id,
		enqueueSnackbar,
		fireAddSongEvent,
		eventData,
		queryClient,
		setOpen,
	]);

	// Feedback filter toggle handlers
	const handleSkillToggle = useCallback(
		(value: string) => {
			fireFilterSkill({ value });
			setSkillFilter((prev) =>
				prev.includes(value)
					? prev.filter((v) => v !== value)
					: [...prev, value],
			);
		},
		[fireFilterSkill],
	);
	const handleGenderToggle = useCallback(
		(value: string) => {
			fireFilterGender({ value });
			setGenderFilter((prev) =>
				prev.includes(value)
					? prev.filter((v) => v !== value)
					: [...prev, value],
			);
		},
		[fireFilterGender],
	);
	const handleGenerationToggle = useCallback(
		(value: string) => {
			fireFilterGeneration({ value });
			setGenerationFilter((prev) =>
				prev.includes(value)
					? prev.filter((v) => v !== value)
					: [...prev, value],
			);
		},
		[fireFilterGeneration],
	);
	const handleServiceToggle = useCallback(
		(value: string) => {
			fireFilterService({ value });
			setServiceFilter((prev) =>
				prev.includes(value)
					? prev.filter((v) => v !== value)
					: [...prev, value],
			);
		},
		[fireFilterService],
	);
	const handleClearFeedbackFilters = useCallback(() => {
		fireClearFilters();
		setSkillFilter([]);
		setGenderFilter([]);
		setGenerationFilter([]);
		setServiceFilter([]);
	}, [fireClearFilters]);

	// Setlist map for O(1) lookup
	const setlistMap = useMemo(
		() => new Map(setlists.map((s) => [s.id, s])),
		[setlists],
	);

	// Setlist IDs that have feedback reports for this specific song
	const songSetlistIds = useMemo(
		() =>
			new Set(
				(openedSong.feedback?.teamSongFeedbackReports ?? []).map(
					(r) => r.setlistId,
				),
			),
		[openedSong.feedback?.teamSongFeedbackReports],
	);

	// Service options derived from setlists this song appears in (hidden if <= 1 unique service)
	const feedbackServiceOptions = useMemo(() => {
		const serviceSet = new Map<string, string>(); // serviceId → serviceName
		Array.from(songSetlistIds).forEach((id) => {
			const sl = setlistMap.get(id);
			const svc = services.find((s) => s.id === sl?.serviceId);
			if (sl?.serviceId && svc) serviceSet.set(sl.serviceId, svc.name);
		});
		return serviceSet.size > 1
			? Array.from(serviceSet.entries()).map(([value, label]) => ({
					label,
					value,
			  }))
			: undefined;
	}, [songSetlistIds, setlistMap, services]);

	// Flat list of per-user submissions for this song across all relevant setlists
	const feedbackSubmissions = useMemo(() => {
		const result: Array<{
			userId: string;
			personalScore: number;
			teamScore: number;
			congregationalScore: number;
			setlistId: string;
		}> = [];
		Array.from(songSetlistIds).forEach((setlistId) => {
			const sl = setlistMap.get(setlistId);
			(sl?.feedback?.submissions ?? []).forEach((sub) => {
				// TODO (team-song-feedback-submissions): Wire up this once we have the team song feedback submissions
				// const songSub = sub.teamSongFeedbackSubmissions.find(
				// 	(ts) => ts.teamSongId === openedSong.id,
				// );
				// if (songSub) {
				// 	result.push({
				// 		userId: sub.userId,
				// 		personalScore: songSub.personalScore,
				// 		teamScore: songSub.teamScore,
				// 		congregationalScore: songSub.congregationalScore,
				// 		setlistId,
				// 	});
				// }
			});
		});
		return result;
	}, [songSetlistIds, setlistMap]);

	// Filter option derivation — only values present in this song's feedback data
	const feedbackSkillOptions = useMemo(() => {
		const skillSet = new Set<string>();
		feedbackSubmissions.forEach((sub) => {
			const member = team.members.find((m) => m.user.id === sub.userId);
			(member?.user.skills ?? []).forEach((s) => skillSet.add(s));
		});
		const arr = Array.from(skillSet)
			.sort()
			.map((s) => ({
				label: s.charAt(0).toUpperCase() + s.slice(1),
				value: s,
			}));
		return arr.length > 1 ? arr : undefined;
	}, [feedbackSubmissions, team.members]);

	const feedbackGenderOptions = useMemo(() => {
		const genderSet = new Set<string>();
		feedbackSubmissions.forEach((sub) => {
			const member = team.members.find((m) => m.user.id === sub.userId);
			if (member?.user.gender) genderSet.add(member.user.gender);
		});
		const arr = Array.from(genderSet)
			.sort()
			.map((g) => ({
				label: g.charAt(0).toUpperCase() + g.slice(1),
				value: g,
			}));
		return arr.length > 1 ? arr : undefined;
	}, [feedbackSubmissions, team.members]);

	const feedbackGenerationOptions = useMemo(() => {
		const genSet = new Set<string>();
		feedbackSubmissions.forEach((sub) => {
			const member = team.members.find((m) => m.user.id === sub.userId);
			const gen = birthYearToGeneration(member?.user.birthYear);
			if (gen !== 'Unknown') genSet.add(gen);
		});
		const arr = Array.from(genSet)
			.sort()
			.map((g) => ({ label: g, value: g }));
		return arr.length > 1 ? arr : undefined;
	}, [feedbackSubmissions, team.members]);

	// Filtered submissions — applies all active filter dimensions
	const filteredSubmissions = useMemo(() => {
		let result = [...feedbackSubmissions];
		if (serviceFilter.length > 0) {
			result = result.filter((sub) => {
				const sl = setlistMap.get(sub.setlistId);
				return sl?.serviceId && serviceFilter.includes(sl.serviceId);
			});
		}
		if (skillFilter.length > 0) {
			result = result.filter((sub) => {
				const member = team.members.find(
					(m) => m.user.id === sub.userId,
				);
				return (member?.user.skills ?? []).some((s) =>
					skillFilter.includes(s),
				);
			});
		}
		if (genderFilter.length > 0) {
			result = result.filter((sub) => {
				const member = team.members.find(
					(m) => m.user.id === sub.userId,
				);
				return (
					member?.user.gender &&
					genderFilter.includes(member.user.gender)
				);
			});
		}
		if (generationFilter.length > 0) {
			result = result.filter((sub) => {
				const member = team.members.find(
					(m) => m.user.id === sub.userId,
				);
				const gen = birthYearToGeneration(member?.user.birthYear);
				return generationFilter.includes(gen);
			});
		}
		return result;
	}, [
		feedbackSubmissions,
		serviceFilter,
		skillFilter,
		genderFilter,
		generationFilter,
		setlistMap,
		team.members,
	]);

	// Filtered setlist IDs — for SentimentOverTime chart and score filtering.
	// When per-user submissions are unavailable, fall back to filtering setlists by service.
	const filteredSetlistIds = useMemo(() => {
		if (filteredSubmissions.length > 0) {
			return new Set(filteredSubmissions.map((s) => s.setlistId));
		}
		if (serviceFilter.length === 0) return new Set<string>();
		const ids = new Set<string>();
		Array.from(songSetlistIds).forEach((id) => {
			const sl = setlistMap.get(id);
			if (sl?.serviceId && serviceFilter.includes(sl.serviceId))
				ids.add(id);
		});
		return ids;
	}, [filteredSubmissions, serviceFilter, songSetlistIds, setlistMap]);

	// Mean scores from filtered submissions — used by Summary scores.
	// Falls back to teamSongFeedbackReports aggregate means until v2.6 wires submissions.
	const filteredMeanScores = useMemo(() => {
		if (filteredSubmissions.length > 0) {
			const personal =
				filteredSubmissions.reduce((s, r) => s + r.personalScore, 0) /
				filteredSubmissions.length;
			const teamScore =
				filteredSubmissions.reduce((s, r) => s + r.teamScore, 0) /
				filteredSubmissions.length;
			const congregation =
				filteredSubmissions.reduce(
					(s, r) => s + r.congregationalScore,
					0,
				) / filteredSubmissions.length;
			const combined = (personal + teamScore + congregation) / 3;
			return {
				combined: Math.round(combined * 10) / 10,
				teamEngagement: Math.round(personal * 10) / 10,
				teamExecution: Math.round(teamScore * 10) / 10,
				churchEngagement: Math.round(congregation * 10) / 10,
			};
		}
		// Fallback: derive from per-setlist report means, scoped to service filter if active
		const allReports = openedSong.feedback?.teamSongFeedbackReports ?? [];
		const reports =
			filteredSetlistIds.size > 0
				? allReports.filter((r) => filteredSetlistIds.has(r.setlistId))
				: allReports;
		if (reports.length === 0) return null;
		const personal =
			reports.reduce((s, r) => s + r.meanPersonalScore, 0) /
			reports.length;
		const teamScore =
			reports.reduce((s, r) => s + r.meanTeamScore, 0) / reports.length;
		const congregation =
			reports.reduce((s, r) => s + r.meanCongregationalScore, 0) /
			reports.length;
		const combined = (personal + teamScore + congregation) / 3;
		return {
			combined: Math.round(combined * 10) / 10,
			teamEngagement: Math.round(personal * 10) / 10,
			teamExecution: Math.round(teamScore * 10) / 10,
			churchEngagement: Math.round(congregation * 10) / 10,
		};
	}, [filteredSubmissions, filteredSetlistIds, openedSong.feedback]);

	// Quartiles for QuartileScales — server-computed when unfiltered, derived when filters are active
	const filteredQuartiles = useMemo(() => {
		const hasActiveFilters =
			serviceFilter.length > 0 ||
			skillFilter.length > 0 ||
			genderFilter.length > 0 ||
			generationFilter.length > 0;
		if (!hasActiveFilters) return openedSong.feedback ?? null;

		const calcQuartile = (values: number[]) => {
			const sorted = [...values].sort((a, b) => a - b);
			const n = sorted.length;
			return {
				q1: sorted[Math.floor(n * 0.25)] ?? sorted[0],
				q2: sorted[Math.floor(n * 0.5)] ?? sorted[0],
				q3: sorted[Math.floor(n * 0.75)] ?? sorted[0],
			};
		};

		if (filteredSubmissions.length > 0) {
			return {
				personalScoreQuartiles: calcQuartile(
					filteredSubmissions.map((s) => s.personalScore),
				),
				teamScoreQuartiles: calcQuartile(
					filteredSubmissions.map((s) => s.teamScore),
				),
				congregationalScoreQuartiles: calcQuartile(
					filteredSubmissions.map((s) => s.congregationalScore),
				),
			};
		}

		// Fallback: filter reports by service and compute from per-setlist means
		const allReports = openedSong.feedback?.teamSongFeedbackReports ?? [];
		const scopedReports =
			filteredSetlistIds.size > 0
				? allReports.filter((r) => filteredSetlistIds.has(r.setlistId))
				: allReports;
		if (scopedReports.length === 0) return null;
		return {
			personalScoreQuartiles: calcQuartile(
				scopedReports.map((r) => r.meanPersonalScore),
			),
			teamScoreQuartiles: calcQuartile(
				scopedReports.map((r) => r.meanTeamScore),
			),
			congregationalScoreQuartiles: calcQuartile(
				scopedReports.map((r) => r.meanCongregationalScore),
			),
		};
	}, [
		openedSong.feedback,
		filteredSubmissions,
		filteredSetlistIds,
		serviceFilter,
		skillFilter,
		genderFilter,
		generationFilter,
	]);

	const songDistributions = useMemo(() => {
		const toSection = (data: { label: string; count: number }[]) => ({
			data,
			emptyReason: null as null,
		});
		return {
			theme: toSection(
				(openedSong.analysisOutput?.thematicKeywords ?? [])
					.flatMap((kw) =>
						kw.theme
							? [{ label: kw.theme, count: kw.strength ?? 0 }]
							: [],
					)
					.sort((a, b) => b.count - a.count),
			),
			theology: toSection(
				(openedSong.analysisOutput?.theologicalKeywords ?? [])
					.flatMap((kw) =>
						kw.theology
							? [{ label: kw.theology, count: kw.strength ?? 0 }]
							: [],
					)
					.sort((a, b) => b.count - a.count),
			),
			gospelStory: toSection(
				openedSong.analysisOutput?.gospelStory.length
					? openedSong.analysisOutput.gospelStory
							.map((g) => ({
								label: g.tag,
								count: g.strength ?? 0,
							}))
							.sort((a, b) => b.count - a.count)
					: (openedSong.gospelTags ?? []).map((tag) => ({
							label: tag,
							count: 1,
					  })),
			),
			emotion: toSection(
				(openedSong.analysisOutput?.emotions ?? [])
					.map((e) => ({ label: e.emotion, count: e.strength ?? 0 }))
					.sort((a, b) => b.count - a.count),
			),
		};
	}, [openedSong.analysisOutput, openedSong.gospelTags]);

	const {
		artworkUrl,
		name,
		artist,
		spotifyId,
		youtubeId,
		appleMusicId,
		defaultKey,
		recordedKey,
		recommendedKey,
		energyTags: rawEnergyTags,
		gospelTags: rawGospelTags,
		songTypeTags: rawSongTypeTags,
		emotionTags: rawEmotionTags,
		feedback,
		lyrics,
	} = openedSong;

	const albumGradient = !artworkUrl ? generateAlbumGradient(name) : null;

	const energyTags = rawEnergyTags ?? [];
	const gospelTags = rawGospelTags ?? [];
	const songTypeTags = rawSongTypeTags ?? [];
	const emotionTags = rawEmotionTags ?? [];

	const allTags = [
		...gospelTags,
		...energyTags,
		...songTypeTags,
		...emotionTags,
	];
	const songTags = allTags.length ? allTags : ['unknown'];
	const hasListenLinks = !!(spotifyId || youtubeId || appleMusicId);

	const hasActions = isTeamSong
		? hasPermission(Permissions.SonglistUpdate) ||
		  !!team.chartRepositoryUrl ||
		  hasListenLinks
		: hasListenLinks || hasPermission(Permissions.SonglistUpdate);

	const CurrentTabContent: React.FC = () => {
		const lyricsFirstLine = lyrics
			? lyrics.split('\n').find((line) => line.trim().length > 0) ?? ''
			: '';
		const lyricsPreview = lyricsFirstLine ? `${lyricsFirstLine}…` : '';

		const lastPlayedISO = useMemo(
			() =>
				setlists
					.filter(
						(s) =>
							!s.draft &&
							new Date(s.startAt) <= new Date() &&
							s.teamSongIds.includes(openedSong.id),
					)
					.sort(
						(a, b) =>
							new Date(b.startAt).getTime() -
							new Date(a.startAt).getTime(),
					)[0]?.startAt ?? null,
			[],
		);
		const lastPlayedFormatted = lastPlayedISO
			? format(new Date(lastPlayedISO), 'd MMMM yyyy')
			: null;

		const effectiveRotationKey = openedSong.new
			? 'high'
			: openedSong.rotationStatus;
		const rotationStatusChip = effectiveRotationKey ? (
			<Chip
				variant="rounded"
				className={getRotationStatusClassName(effectiveRotationKey)}
			>
				{ROTATION_STATUSES.find((s) => s.key === effectiveRotationKey)
					?.capitalisedLabel ?? effectiveRotationKey}
			</Chip>
		) : undefined;

		const detailsItems: SettingsListItems = [
			{
				type: 'setting',
				title: 'Lyrics',
				subtitle: lyricsPreview || 'No lyrics available',
				onClick: lyrics
					? () => pushDisplayState(ESongModalDisplayState.LYRICS)
					: undefined,
			},
			{
				type: 'setting',
				title: 'Energy',
				trailingComponent: (
					<div
						style={{
							display: 'flex',
							gap: '4px',
							flexWrap: 'wrap',
							justifyContent: 'flex-end',
						}}
					>
						{energyTags.map((tag) => (
							<SongThemeChip key={tag} themeTag={tag} />
						))}
					</div>
				),
				onClick: () => setInfoSheet('energy'),
				hidden: !energyTags.length,
			},
			{
				type: 'setting',
				title: 'Song Type',
				trailingComponent: (
					<div
						style={{
							display: 'flex',
							gap: '4px',
							flexWrap: 'wrap',
							justifyContent: 'flex-end',
						}}
					>
						{songTypeTags.map((tag) => (
							<SongThemeChip key={tag} themeTag={tag} />
						))}
					</div>
				),
				onClick: () => setInfoSheet('songType'),
				hidden: !songTypeTags.length,
			},
			{ type: 'title', title: 'Key' },
			{
				type: 'setting',
				title: 'Default Key',
				trailingComponent: defaultKey ? (
					<div className={styles['key-badge']}>{defaultKey}</div>
				) : undefined,
				subtitle:
					isTeamSong && hasPermission(Permissions.SonglistUpdate)
						? 'Select to edit'
						: undefined,
				onClick:
					isTeamSong && hasPermission(Permissions.SonglistUpdate)
						? () =>
								pushDisplayState(
									ESongModalDisplayState.DEFAULT_KEY,
								)
						: undefined,
				hidden: !isTeamSong && !defaultKey,
			},
			{
				type: 'setting',
				title: 'Recorded Key',
				trailingComponent: recordedKey ? (
					<div className={styles['key-badge']}>{recordedKey}</div>
				) : undefined,
				hidden: !recordedKey,
			},
			{ type: 'title', title: 'Rotation', hidden: !isTeamSong },
			{
				type: 'setting',
				title: 'Last played',
				subtitle: lastPlayedFormatted ?? 'Never played',
				trailingComponent: lastPlayedISO ? (
					<SetlistSummaryCalendar
						startAtISO={lastPlayedISO}
						size="md"
					/>
				) : undefined,
				hidden: !isTeamSong,
			},
			{
				type: 'setting',
				title: 'Rotation status',
				subtitle: hasPermission(Permissions.SonglistUpdate)
					? openedSong.new
						? 'Unmark as new to edit'
						: 'Select to edit'
					: undefined,
				onClick: hasPermission(Permissions.SonglistUpdate)
					? openedSong.new
						? undefined
						: () =>
								pushDisplayState(
									ESongModalDisplayState.ROTATION_STATUS,
								)
					: undefined,
				disabled: hasPermission(Permissions.SonglistUpdate)
					? !!openedSong.new
					: undefined,
				trailingComponent: rotationStatusChip,
				hidden: !isTeamSong,
			},
			{
				type: 'setting',
				title: 'Mark as new',
				subtitle: 'High rotation to introduce song',
				trailingComponent: (
					<AsaphSwitch
						checked={openedSong.new ?? false}
						onChange={(e) => {
							const isNew = e.target.checked;
							updateTeamSong(
								isNew
									? { new: true, rotationStatus: 'high' }
									: { new: false },
							);
						}}
					/>
				),
				hidden:
					!isTeamSong || !hasPermission(Permissions.SonglistUpdate),
			},
		];

		switch (tabIndex) {
			case 0:
				return <SettingsList items={detailsItems} />;

			case 1: {
				const ao = openedSong.analysisOutput;
				const hasAnalysis = !!(
					ao &&
					(ao.summary ||
						ao.emotions.length > 0 ||
						ao.thematicKeywords.length > 0 ||
						ao.theologicalKeywords.length > 0 ||
						(ao.gospelStory?.length ?? 0) > 0)
				);

				const hasEnoughLyrics = !!lyrics && lyrics.length > 100;
				const canGenerate =
					(isTeamSong ? !!openedSong.originSongId : true) &&
					hasEnoughLyrics;
				const generateButton = canGenerate ? (
					<AsaphButton
						color="gradient"
						label="Generate Analysis"
						onClick={() => {
							fireRequestAnalysisEvent(eventData);
							reanalyzeSong({
								songId:
									openedSong.originSongId ?? openedSong.id,
							});
						}}
						isLoading={isReanalyzing}
						fullWidth
					/>
				) : null;

				if (!hasAnalysis) {
					return (
						<List>
							<div className={styles['analysis-placeholder']}>
								<div
									className={
										styles['analysis-placeholder__icon']
									}
								>
									✦
								</div>
								<div
									className={
										styles['analysis-placeholder__title']
									}
								>
									No AI analysis found for this song
								</div>
								{generateButton}
							</div>
						</List>
					);
				}

				const sectionEmpty = (title: string) => (
					<ModalSectionCard paddingSize="md">
						<ModalSectionTitle>{title}</ModalSectionTitle>
						<div style={{ marginBottom: '20px' }}>
							<ModalSectionSecondaryText>
								No AI analysis found for this report
							</ModalSectionSecondaryText>
						</div>
						{generateButton}
					</ModalSectionCard>
				);

				return (
					<List>
						<ModalSectionCard paddingSize="md">
							<ModalSectionTitle>Song summary</ModalSectionTitle>
							{ao?.summary ? (
								<RenderRichText text={ao.summary} />
							) : (
								<>
									<div style={{ marginBottom: '20px' }}>
										<ModalSectionSecondaryText>
											{hasEnoughLyrics
												? 'No AI summary found for this song'
												: "No AI summary found for this song. This song's lyrics are not long enough for generation to happen."}
										</ModalSectionSecondaryText>
									</div>
									{generateButton}
								</>
							)}
						</ModalSectionCard>
						<SettingSectionTitle type="title" title="Reports" />
						{songDistributions.theme.data.length > 0 ? (
							<DistributionCirclePackingChart
								title="Themes"
								section={songDistributions.theme}
								defaultView="bubble"
								singleSource
							/>
						) : (
							sectionEmpty('Themes')
						)}
						{songDistributions.theology.data.length > 0 ? (
							<DistributionCirclePackingChart
								title="Theology"
								section={songDistributions.theology}
								defaultView="word-cloud"
								singleSource
							/>
						) : (
							sectionEmpty('Theology')
						)}
						{songDistributions.gospelStory.data.length > 0 ? (
							<DistributionCirclePackingChart
								title="Gospel Story"
								section={songDistributions.gospelStory}
								defaultView="rank"
								semanticColors
								singleSource
							/>
						) : (
							sectionEmpty('Gospel Story')
						)}
						{songDistributions.emotion.data.length > 0 ? (
							<DistributionCirclePackingChart
								title="Emotions"
								section={songDistributions.emotion}
								semanticColors
								singleSource
							/>
						) : (
							sectionEmpty('Emotions')
						)}
					</List>
				);
			}
			case 2: {
				if (!openedSong.feedback?.teamSongFeedbackReports?.length) {
					const songSetlists = setlists.filter((s) =>
						s.teamSongIds.includes(openedSong.id),
					);
					const isLeader = hasPermission(Permissions.SetlistCreate);
					const emptyStateConfig = getFeedbackEmptyStateConfig(
						songSetlists,
						isLeader,
						services,
					);

					if (!emptyStateConfig) {
						return (
							<List>
								<ModalSectionCard paddingSize="md">
									<ModalSectionTitle centered>
										No reports yet
									</ModalSectionTitle>
									<ModalSectionSecondaryText>
										Add this song to a setlist and mark the
										service complete to see reports here.
									</ModalSectionSecondaryText>
								</ModalSectionCard>
							</List>
						);
					}

					return (
						<List>
							<ModalSectionCard paddingSize="md">
								<ModalSectionTitle centered>
									{emptyStateConfig.heading}
								</ModalSectionTitle>
								<ModalSectionSecondaryText>
									{emptyStateConfig.body}
								</ModalSectionSecondaryText>
								{emptyStateConfig.ctaLabel &&
									emptyStateConfig.ctaSetlistId && (
										<div
											className={
												styles[
													'feedback-empty-state-cta'
												]
											}
										>
											<AsaphButton
												color="blue"
												label={
													emptyStateConfig.ctaLabel
												}
												onClick={() =>
													setFeedbackSetlistId(
														emptyStateConfig.ctaSetlistId!,
													)
												}
											/>
										</div>
									)}
							</ModalSectionCard>
						</List>
					);
				}

				return (
					<>
						{(feedbackSkillOptions ||
							feedbackGenderOptions ||
							feedbackGenerationOptions ||
							feedbackServiceOptions) && (
							<div className={styles['feedback-filter-bar']}>
								<ReportsFilterBar
									skillFilter={skillFilter}
									onSkillToggle={handleSkillToggle}
									skillOptions={feedbackSkillOptions}
									genderFilter={genderFilter}
									onGenderToggle={handleGenderToggle}
									genderOptions={feedbackGenderOptions}
									generationFilter={generationFilter}
									onGenerationToggle={handleGenerationToggle}
									generationOptions={
										feedbackGenerationOptions
									}
									serviceFilter={serviceFilter}
									onServiceToggle={handleServiceToggle}
									serviceOptions={feedbackServiceOptions}
									onClearAll={handleClearFeedbackFilters}
								/>
							</div>
						)}
						<List>
							<SettingSectionTitle type="title" title="Summary" />
							<SettingsList
								items={[
									{
										type: 'setting',
										title: 'Combined average',
										trailingComponent:
											filteredMeanScores ? (
												<div
													className={
														styles[
															'feedback-score-badge'
														]
													}
												>
													{filteredMeanScores.combined.toFixed(
														1,
													)}
												</div>
											) : undefined,
									},
									{
										type: 'setting',
										title: 'Team engagement',
										trailingComponent:
											filteredMeanScores ? (
												<div
													className={
														styles[
															'feedback-score-badge'
														]
													}
												>
													{filteredMeanScores.teamEngagement.toFixed(
														1,
													)}
												</div>
											) : undefined,
									},
									{
										type: 'setting',
										title: 'Team execution',
										trailingComponent:
											filteredMeanScores ? (
												<div
													className={
														styles[
															'feedback-score-badge'
														]
													}
												>
													{filteredMeanScores.teamExecution.toFixed(
														1,
													)}
												</div>
											) : undefined,
									},
									{
										type: 'setting',
										title: 'Church engagement',
										trailingComponent:
											filteredMeanScores ? (
												<div
													className={
														styles[
															'feedback-score-badge'
														]
													}
												>
													{filteredMeanScores.churchEngagement.toFixed(
														1,
													)}
												</div>
											) : undefined,
									},
								]}
							/>

							{/* Reports section */}
							<SettingSectionTitle type="title" title="Reports" />
							<SentimentOverTime
								openedSong={openedSong}
								filteredSetlistIds={filteredSetlistIds}
							/>
							<ModalSectionCard
								paddingSize="lg"
								className={styles['insights']}
							>
								<ModalSectionTitle>
									Average Sentiment
								</ModalSectionTitle>
								<QuartileScales quartiles={filteredQuartiles} />
							</ModalSectionCard>
						</List>
					</>
				);
			}
			default:
				return null;
		}
	};

	return (
		<>
			<BottomActionLayout
				contentClassName={styles['content']}
				actionHidden={!hasActions}
				onScroll={(scrollTop) => {
					let newTitleVisibleValue = false;
					if (scrollTop > TOP_ITEMS_HEIGHT) {
						newTitleVisibleValue = true;
					} else {
						newTitleVisibleValue = false;
					}
					if (titleVisible !== newTitleVisibleValue) {
						setTitleVisible(newTitleVisibleValue);
					}
				}}
				noPadding
				actionDivider
				actionContent={
					isTeamSong ? (
						<>
							{hasPermission(Permissions.SonglistUpdate) &&
								(!team.chartRepositoryUrl && !hasListenLinks ? (
									<AsaphButton
										color="grey"
										label="Song Settings"
										onClick={onSettingsClick}
										fullWidth
									/>
								) : (
									<IconButton
										onClick={onSettingsClick}
										className={styles['settings-button']}
									>
										<SvgIcon
											component={more}
											fontSize="small"
										/>
									</IconButton>
								))}
							{!!team.chartRepositoryUrl && (
								<AsaphButton
									color="grey"
									label="Go to Charts"
									onClick={handleGoToChards}
									className={styles['manage-button']}
									fullWidth
								/>
							)}

							{hasListenLinks && (
								<AsaphButton
									color="blue"
									label="Listen"
									onClick={() => setIsListenOpen(true)}
									fullWidth
								/>
							)}
						</>
					) : (
						<>
							{hasListenLinks && (
								<AsaphButton
									color="grey"
									label="Listen"
									onClick={() => setIsListenOpen(true)}
									className={styles['manage-button']}
									fullWidth
								/>
							)}
							{hasPermission(Permissions.SonglistUpdate) && (
								<AsaphButton
									color="blue"
									label="Add song"
									onClick={handleAdd}
									fullWidth
									disabled={isLoading}
								/>
							)}
						</>
					)
				}
				bottomSheetContent={
					<div className={styles['listen-links']}>
						<div className={styles['title']}>
							{!(spotifyId || youtubeId || appleMusicId)
								? `No links have been added for this song`
								: `Select your prefered listening service`}
						</div>
						<div className={styles['links']}>
							{spotifyId && (
								<Link
									onClick={() =>
										fireOpenSpotifyEvent(eventData)
									}
									to={`${
										spotifyId.includes('spotify')
											? spotifyId
											: `https://open.spotify.com/track/${spotifyId}`
									}`}
									target="_blank"
								>
									<Spotify />
								</Link>
							)}
							{youtubeId && (
								<Link
									onClick={() => fireOpenYTEvent(eventData)}
									to={`${
										youtubeId.includes('youtu')
											? youtubeId
											: `https://www.youtube.com/watch?v=${youtubeId}`
									}`}
									target="_blank"
								>
									{isDarkModeOn ? (
										<YouTubeWhite />
									) : (
										<YouTubeBlack />
									)}
								</Link>
							)}
							{appleMusicId && (
								<Link
									onClick={() =>
										fireOpenAppleMusicEvent(eventData)
									}
									to={`${
										appleMusicId.includes('apple')
											? appleMusicId
											: `https://music.apple.com/us/album/when-we-pray-single/${appleMusicId}`
									}`}
									target="_blank"
								>
									{isDarkModeOn ? (
										<AppleMusicWhite />
									) : (
										<AppleMusicBlack />
									)}
								</Link>
							)}
						</div>
					</div>
				}
				bottomSheetOpen={isListenOpen}
				onBottomSheetClose={() => setIsListenOpen(false)}
			>
				<div className={styles['song-modal-root']}>
					<div
						className={styles['centered']}
						//style={{ minHeight: TOP_ITEMS_HEIGHT }}
					>
						<div
							className={styles['cover-image']}
							style={
								albumGradient ? albumGradient.style : undefined
							}
						>
							{artworkUrl ? (
								<img src={artworkUrl} />
							) : (
								<span
									style={
										albumGradient
											? { color: albumGradient.textColor }
											: undefined
									}
								>
									{getAcronym(name)}
								</span>
							)}
						</div>
						<div className={styles['title']}>{name}</div>
						<div className={styles['artist']}>{artist}</div>
					</div>
					<Tabs
						className={styles.tabs}
						onChange={(index) => {
							const TAB_NAMES = [
								'Details',
								'Analysis',
								'Feedback',
							];
							fireViewTab({
								tab: TAB_NAMES[index] ?? String(index),
							});
							setTabIndex(index);
						}}
						items={[
							{ title: 'Details' },
							{ title: 'Analysis' },
							...(isTeamSong
								? [
										{
											title: 'Feedback',
											trailingComponent:
												sentimentReports.length >=
													2 && (
													<div
														className={
															styles['trend']
														}
													>
														<TrendIcon
															trend={deriveTrend(
																sentimentReports,
															)}
														/>
													</div>
												),
										},
								  ]
								: []),
						]}
					/>

					{CurrentTabContent({})}
				</div>
			</BottomActionLayout>

			{infoSheet === 'energy' && (
				<BottomCard title="Energy" onClose={() => setInfoSheet(null)}>
					<BottomCardForm>
						<div className={styles['info-sheet-body']}>
							<p>
								<strong>Upbeat:</strong> Songs that are
								high-energy and celebratory, encouraging vibrant
								singing.
							</p>
							<p>
								<strong>Building:</strong> Songs that gradually
								increase in intensity and momentum, leading to
								crescendo.
							</p>
							<p>
								<strong>Settling:</strong> Songs that end in a
								contemplative place, creating space for what
								comes next.
							</p>
							<p>
								<strong>Reflective:</strong> Songs that are
								contemplative, creating space for reflection or
								response.
							</p>
						</div>
					</BottomCardForm>
					<BottomCardAction>
						<AsaphButton
							onClick={() => setInfoSheet(null)}
							fullWidth
							label="OK, got it"
							color="blue"
						/>
					</BottomCardAction>
				</BottomCard>
			)}

			{infoSheet === 'songType' && (
				<BottomCard
					title="Song Type"
					onClose={() => setInfoSheet(null)}
				>
					<BottomCardForm>
						<div className={styles['info-sheet-body']}>
							<p>
								<strong>Hymn:</strong> Verse-based songs that
								progress through a narrative or theme.
							</p>
							<p>
								<strong>Chorus:</strong> Songs built around a
								repeating refrain or chorus section.
							</p>
						</div>
					</BottomCardForm>
					<BottomCardAction>
						<AsaphButton
							onClick={() => setInfoSheet(null)}
							fullWidth
							label="OK, got it"
							color="blue"
						/>
					</BottomCardAction>
				</BottomCard>
			)}

			{feedbackSetlistId && (
				<FeedbackPanel
					onClose={({ submittedFeedback }) => {
						setFeedbackSetlistId(null);
						if (submittedFeedback) {
							queryClient.invalidateQueries({
								queryKey: ['ListMyTeamSongs'],
							});
						}
					}}
					config={{ setlistId: feedbackSetlistId }}
				/>
			)}
		</>
	);
};

const SENTIMENT_BLUE = '#0040ff';
const DEFAULT_LAYERS_SENTIMENT = [
	'grid',
	'markers',
	'axes',
	'areas',
	'crosshair',
	'lines',
	'points',
	'slices',
	'mesh',
	'legends',
] as const;

const SentimentOverTime: React.FC<{
	openedSong: SongModalSong;
	filteredSetlistIds?: Set<string>;
}> = ({ openedSong, filteredSetlistIds }) => {
	const reports = useMemo(
		() => {
			const rawReports =
				openedSong.feedback?.teamSongFeedbackReports ?? [];
			const scoped =
				filteredSetlistIds && filteredSetlistIds.size > 0
					? rawReports.filter((r) =>
							filteredSetlistIds.has(r.setlistId),
					  )
					: rawReports;
			return prepareSentimentReports(scoped);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			openedSong.feedback?.teamSongFeedbackReports?.length,
			filteredSetlistIds,
		],
	);

	const data = useMemo(
		() => [
			{
				id: 'personal',
				color: '#fff',
				data: reports.map((r) => ({
					x: getFormattedXValue(r.startAt),
					y: r.meanPersonalScore,
				})),
			},
			{
				id: 'team',
				color: '#000',
				data: reports.map((r) => ({
					x: getFormattedXValue(r.startAt),
					y: r.meanTeamScore,
				})),
			},
			{
				id: 'congregation',
				color: '#fefe',
				data: reports.map((r) => ({
					x: getFormattedXValue(r.startAt),
					y: r.meanCongregationalScore,
				})),
			},
		],
		[reports],
	);

	const trendLine = useMemo(() => {
		const n = reports.length;
		if (n < 2) return null;
		const ys = reports.map(
			(r) =>
				(r.meanPersonalScore +
					r.meanTeamScore +
					r.meanCongregationalScore) /
				3,
		);
		const sumX = (n * (n - 1)) / 2;
		const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6;
		const sumY = ys.reduce((a, b) => a + b, 0);
		const sumXY = ys.reduce((acc, y, i) => acc + i * y, 0);
		const denom = n * sumX2 - sumX * sumX;
		if (denom === 0) return null;
		const slope = (n * sumXY - sumX * sumY) / denom;
		const intercept = (sumY - slope * sumX) / n;

		// Snap to flat when movement is below threshold — keeps the line
		// consistent with the Flat icon shown in the tab and on the card.
		const isFlat = Math.abs(slope * (n - 1)) < FLAT_THRESHOLD;
		const meanY = sumY / n;

		return {
			startX: getFormattedXValue(reports[0].startAt),
			startY: isFlat ? meanY : intercept,
			endX: getFormattedXValue(reports[n - 1].startAt),
			endY: isFlat ? meanY : slope * (n - 1) + intercept,
		};
	}, [reports]);

	const trendLayer = useMemo(() => {
		if (!trendLine) return null;
		const { startX, startY, endX, endY } = trendLine;
		return function TrendLineLayer({
			xScale,
			yScale,
		}: {
			xScale: (v: unknown) => number;
			yScale: (v: unknown) => number;
		}) {
			return (
				<line
					x1={xScale(startX)}
					y1={yScale(startY)}
					x2={xScale(endX)}
					y2={yScale(endY)}
					stroke={SENTIMENT_BLUE}
					strokeWidth={2}
				/>
			);
		};
	}, [trendLine]);

	const layers = useMemo(
		() =>
			trendLayer
				? [...DEFAULT_LAYERS_SENTIMENT, trendLayer]
				: [...DEFAULT_LAYERS_SENTIMENT],
		[trendLayer],
	);

	if (!reports.length || !data.every((s) => s.data.length > 1)) {
		return null;
	}

	return (
		<ModalSectionCard paddingSize="lg" className={styles['insights']}>
			<ModalSectionTitle>Sentiment History</ModalSectionTitle>

			<Legend>
				<LegendItem
					label={'Personal'}
					className="px-color-scheme-red"
				/>
				<LegendItem label={'Team'} className="px-color-scheme-green" />
				<LegendItem
					label={'Congregation'}
					className="px-color-scheme-purple"
				/>
				<div className={styles['trend-legend-item']}>
					<div className={styles['trend-legend-circle']} />
					<span className={styles['trend-legend-label']}>Trend</span>
				</div>
			</Legend>

			<LineChart
				wrapperClassName={styles['line-chart']}
				data={data}
				emojiYAxis
				layers={layers as any}
			/>
		</ModalSectionCard>
	);
};

const epochISODate = new Date(0).toISOString();

const getFormattedXValue = (ISOString: string) =>
	ISOString === epochISODate
		? ''
		: `${format(new Date(ISOString), 'dd MMM')}`;

const byStartAtDate = groupBy((record: LimitedTeamSongFeedbackReport) => {
	let ISOStringDate;
	try {
		ISOStringDate = format(new Date(record.startAt), 'yyyy-MM-dd');
	} catch (err) {
		ISOStringDate = '1970-01-01';
	}
	return ISOStringDate;
});

// Shared data preparation used by both the tab trend icon and SentimentOverTime.
// Applies the same slice + same-day grouping so both surfaces see identical data.
const prepareSentimentReports = (
	rawReports: LimitedTeamSongFeedbackReport[],
): LimitedTeamSongFeedbackReport[] => {
	const sliced = rawReports.slice(-5);
	const byDate = byStartAtDate(sliced);
	const res: LimitedTeamSongFeedbackReport[] = [];
	for (const [, setlistData] of Object.entries(byDate)) {
		if (!setlistData[0]) continue;
		res.push({
			...setlistData[0],
			meanPersonalScore: mean(
				...setlistData.map((r) => r.meanPersonalScore),
			) as number,
			meanTeamScore: mean(
				...setlistData.map((r) => r.meanTeamScore),
			) as number,
			meanCongregationalScore: mean(
				...setlistData.map((r) => r.meanCongregationalScore),
			) as number,
		});
	}
	return res;
};

type RenderRichTextProps = {
	text: string;
	className?: string;
	strongProps?: object;
	sectionProps?: object;
	ulProps?: object;
	liProps?: object;
};

export const RenderRichText: React.FC<RenderRichTextProps> = ({
	className,
	text,
	strongProps,
	sectionProps,
	ulProps,
	liProps,
}) => {
	const classes = [styles['render-rich-text'], className].filter(Boolean);

	return (
		<div className={classes.join(' ')}>
			<ReactMarkdown
				components={{
					p: ({ children }) => {
						return (
							<p className={`${styles['p']}`} {...sectionProps}>
								{children}
							</p>
						);
					},
					br: ({ children }) => {
						return <br />;
					},
				}}
			>
				{text}
			</ReactMarkdown>
		</div>
	);
};
